import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { openDetailRowSx } from '../utils/personalFinancesHousehold';

export interface HouseholdSimilarTxRowView {
  id: number;
  when: string;
  description: string;
  debit: string;
  credit: string;
  category: string;
  isAnchor: boolean;
}

interface Props {
  open: boolean;
  needle: string;
  rows: HouseholdSimilarTxRowView[];
  onClose: () => void;
  onSelectRow: (id: number) => void;
}

export const HouseholdSimilarTxDialog: React.FC<Props> = ({
  open,
  needle,
  rows,
  onClose,
  onSelectRow,
}) => {
  const otherCount = Math.max(0, rows.length - 1);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      transitionDuration={0}
      sx={{ zIndex: (theme) => theme.zIndex.snackbar + 25 }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          pr: 1,
        }}
      >
        Similar transactions
        <IconButton aria-label="Close similar transactions" edge="end" size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers data-testid="household-similar-tx-dialog">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {otherCount === 0
            ? `No other loaded rows match “${needle || 'this transaction'}”.`
            : `${otherCount} other loaded ${otherCount === 1 ? 'row matches' : 'rows match'} “${needle}”.`}
        </Typography>
        <TableContainer component={Box}>
          <Table size="small" stickyHeader aria-label="Similar transactions">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Debit</TableCell>
                <TableCell align="right">Credit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={row.isAnchor}
                  data-testid={`household-similar-tx-row-${row.id}`}
                  onClick={() => onSelectRow(row.id)}
                  sx={{
                    cursor: 'pointer',
                    ...(row.isAnchor ? openDetailRowSx : {}),
                  }}
                >
                  <TableCell>{row.id}</TableCell>
                  <TableCell>
                    <Tooltip title={row.when} disableHoverListener={!row.when}>
                      <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.when.replace(/ \d{2}:\d{2}$/, '')}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.description || '—'}</TableCell>
                  <TableCell>{row.category}</TableCell>
                  <TableCell align="right">{row.debit}</TableCell>
                  <TableCell align="right">{row.credit}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};
